/**
 * The day-by-day FuelEU calculator's shapes and loaders.
 *
 * Thin, like `fueleu.ts` and for the same reason: none of the regulation's
 * arithmetic lives here. The cumulative balance, the quarters, the halves and
 * the biofuel requirement all come from `fueleu_daily_calculator/preview/`,
 * which runs the same `ecosail/fueleu.py` the annual calculator does — so the
 * two tabs cannot disagree about the same mix.
 *
 * The fuel rows themselves are `fueleu.ts`'s `FuelEURow`, unchanged. The only
 * difference between the two forms is what a quantity means: tonnes bunkered
 * over the period there, tonnes burnt per day here — and, for the already-
 * consumed table, tonnes burnt over the stretch that has gone. That difference
 * lives in `buildDailyPayload` (which renames the field on the way out and
 * stamps the phase) and in the unit label on screen, and nowhere else.
 */
import { globalAPI } from "./global";
import { queryKeys } from "./queryKeys";
import {
  FuelEURow,
  FuelEUResultFuel,
  FuelEUSensitivityRow,
  emptyFuelRow,
  expandBioBlend,
  isBioBlend,
  numberOrNull,
} from "./fueleu";

/** The default horizon: a whole compliance year. */
export const DEFAULT_DAYS = 365;
/** Art. 15's compliance period is the calendar year, so the plan stops there. */
export const MAX_DAYS = 365;

/** Horizons worth one click. Roughly a month, a quarter, a half and the year. */
export const DAY_PRESETS = [30, 90, 180, 365];

/**
 * How much of the period might already have gone. The same ladder minus the
 * year: a plan whose whole horizon is already consumed has nothing left to
 * plan, which the API rejects rather than answers.
 */
export const ELAPSED_PRESETS = [30, 90, 180];

/**
 * How far ahead to plan from there. The "rest of the year" option is added at
 * render time, because its length depends on how much has already gone.
 */
export const PLAN_PRESETS = [30, 90, 180];

/* ---------------------------------------------------------------- the mode */

/**
 * Which of the two questions the form is answering.
 *
 * A switch rather than two sections that both offer a number of days, because
 * stacked they read as a contradiction — a "planning horizon" of 1 day above
 * "30 days already consumed" is not a plan, and nothing on screen says which
 * number is supposed to contain the other.
 *
 * The API has no mode. It takes one horizon (`days`) and an offset into it
 * (`elapsed_days`), and MODE_PARTIAL's two boxes are added together on the way
 * out. The form asks the question the operator is actually holding — "I have
 * burnt 90 days, plan me the next 90" — and `buildDailyPayload` does the sum.
 */
export const MODE_FULL = "full";
export const MODE_PARTIAL = "partial";

export const isPartial = (values: any): boolean =>
  values?.mode === MODE_PARTIAL;

/**
 * The number of days being planned forward, whichever mode is in force.
 *
 * In MODE_FULL that is the whole horizon; in MODE_PARTIAL it is the window
 * after the elapsed stretch. Shared by the payload builder and the form, so the
 * days the screen says it is planning and the days it posts cannot drift.
 */
export const planWindow = (values: any): number => {
  const raw = isPartial(values) ? values?.plan_days : values?.days;
  const days = Number(raw);
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
};

/** The elapsed stretch in force. Always 0 outside MODE_PARTIAL. */
export const elapsedWindow = (values: any): number => {
  if (!isPartial(values)) return 0;
  const days = Number(values?.elapsed_days);
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
};

/** One day of the plan, cumulative from day 1. */
export interface FuelEUDailyEntry {
  day: number;
  /** How many of the days to here are still the plan's to change. */
  remaining_days: number;
  total_energy: number;
  total_emissions: number;
  attained_intensity: number;
  target_intensity: number;
  compliance_balance: number;
  penalty_eur: number;
  is_compliant: boolean;
  status: "Surplus" | "Deficit" | "Break-even";
  /**
   * Null, never 0, when no quantity of the chosen fuel reaches the target —
   * "none suffices" is the opposite news from "none needed", and both happen.
   * `requirement_reason` says which.
   */
  required_alternative_mass: number | null;
  required_alternative_share: number | null;
  displaced_fossil_mass: number | null;
  requirement_reason: string | null;
  /** That day's own share of the requirement. Not clamped at zero. */
  incremental_alternative_mass: number | null;
}

/**
 * The status to show against one row of a table that runs to the horizon.
 *
 * Every row but the last is a waypoint, and its status is the balance as the
 * plan passes it. The last row is the horizon, where a requirement of
 * `requirement` reaches break-even — so labelling it "Deficit" argues with the
 * remedy the same plan prescribes. There it is shown as the position that
 * requirement arrives at.
 *
 * Conditional on the requirement existing. Where no quantity of the chosen fuel
 * reaches the target there is no break-even on offer, and the row keeps the
 * deficit it really has. A horizon already in surplus keeps that too — there is
 * nothing to reach.
 *
 * Shared by the checkpoint table and the quarters, which run to the same day and
 * so must agree about what happens on it.
 */
export const horizonStatus = (
  status: FuelEUDailyEntry["status"],
  requirement: number | null,
  isHorizon: boolean
): FuelEUDailyEntry["status"] =>
  isHorizon && status === "Deficit" && requirement !== null
    ? "Break-even"
    : status;

/** A quarter or a half, with both the cumulative and its own-period view. */
export interface FuelEUPeriodRow {
  label: string;
  start_day: number;
  end_day: number;
  days: number;
  /** True where the horizon cut the period short of its calendar length. */
  truncated: boolean;
  cumulative_energy: number;
  cumulative_emissions: number;
  cumulative_balance: number;
  cumulative_penalty_eur: number;
  cumulative_alternative_mass: number | null;
  attained_intensity: number;
  target_intensity: number;
  status: "Surplus" | "Deficit" | "Break-even";
  period_energy: number;
  period_emissions: number;
  period_balance: number;
  period_alternative_mass: number | null;
  /** Why there is no requirement, where there is none. */
  requirement_reason: string | null;
}

export interface FuelEUDailySummary {
  days: number;
  /** The split. `elapsed_days` is 0 for a plan that starts on day one. */
  elapsed_days: number;
  remaining_days: number;
  total_energy: number;
  total_emissions: number;
  attained_intensity: number;
  target_intensity: number;
  compliance_balance: number;
  penalty_eur: number;
  is_compliant: boolean;
  status: "Surplus" | "Deficit" | "Break-even";
  required_alternative_mass: number | null;
  required_alternative_share: number | null;
  displaced_fossil_mass: number | null;
  requirement_reason: string | null;
  /** The day the accrued balance first goes negative. Null if it never does. */
  first_deficit_day: number | null;
  /**
   * Where the balance already stood when the plan took over — the position the
   * remaining days inherit. Null, not zero, where nothing has been consumed:
   * "starts level" and "has no opening" are different facts.
   */
  opening_balance: number | null;
  opening_penalty_eur: number | null;
  opening_energy: number | null;
  opening_intensity: number | null;
  /** Averaged over the days that are LEFT, not over the horizon. */
  alternative_mass_per_day: number | null;
  penalty_per_day: number | null;
}

export interface FuelEUDailyResult {
  days: number;
  elapsed_days: number;
  remaining_days: number;
  summary: FuelEUDailySummary;
  reference_intensity: number;
  reduction: number;
  target_intensity: number;
  /** The mix over the whole horizon — per-day rate times days. */
  fuels: FuelEUResultFuel[];
  fuel_energy: number;
  fuel_intensity: number;
  has_unpriced_fuel: boolean;
  unpriced_fuels: number;
  unpriced_energy: number;
  ops_energy: number;
  alternative_fuel: string | null;
  alternative_intensity: number | null;
  alternative_lcv: number | null;
  displaced_fuel: string | null;
  displaced_reason: string | null;
  /** The curve's points. Eight or fewer, always ending on the horizon. */
  checkpoints: FuelEUDailyEntry[];
  /** The elapsed/remaining split. Empty where nothing has been consumed. */
  phases: FuelEUPeriodRow[];
  quarters: FuelEUPeriodRow[];
  halves: FuelEUPeriodRow[];
  /**
   * The blend ladder at the horizon — the same table the annual results screen
   * carries. Shares are fractions of the whole period's energy, which is what
   * the balance is scored on, but the fuel only goes into the remaining days, so
   * the ladder stops at what those days can absorb.
   */
  sensitivity: FuelEUSensitivityRow[];
  /** Empty on a preview — see the viewset's `include_daily`. */
  daily: FuelEUDailyEntry[];
}

/**
 * The request body both `preview` and `create` take.
 *
 * `quantity_per_day` rather than `quantity` is the whole difference from
 * `buildPayload`. The blend expansion is reused rather than rewritten: what a
 * blend splits into is the same decision in both calculators, and the only thing
 * that changes is the name of the field the tonnage lands in.
 *
 * The form holds two tables and the API takes one list: the already-consumed
 * rows are stamped `phase: "consumed"` and carry `quantity_total`, the planned
 * ones `phase: "planned"` and `quantity_per_day`. One list because Annex I has
 * no concept of a phase either — it has a list of fuels, and which side of the
 * split a row sits on only decides how `fueleu.cumulative_rows` scales it.
 *
 * The consumed rows are dropped entirely outside MODE_PARTIAL, so a table left
 * filled in before the operator switched back cannot quietly add a quarter of a
 * year's fuel to the mix. The API does the same on its side
 * (`FuelEUDailyCalculator.consumed_entries`); this keeps the preview honest
 * before the request is even made.
 *
 * `days` is the sum of the two windows, which is the one place the form's shape
 * and the API's shape are reconciled.
 */
export const buildDailyPayload = (values: any) => {
  const elapsed = elapsedWindow(values);
  const plan = planWindow(values);

  const asPlanned = (part: any) => {
    const { quantity, ...rest } = part;
    return { ...rest, phase: "planned", quantity_per_day: quantity };
  };
  const asConsumed = (part: any) => {
    const { quantity, ...rest } = part;
    return { ...rest, phase: "consumed", quantity_total: quantity };
  };

  const expand = (rows: FuelEURow[], shape: (part: any) => any): any[] =>
    (rows ?? [])
      // A row the user added and never filled in is not an input.
      .filter((row: FuelEURow) => row.fuel_factor && Number(row.quantity) > 0)
      // Annotated `any[]`, because a blend expands to two rows shaped slightly
      // differently from a loose fuel and TypeScript otherwise narrows the union
      // to whichever branch it saw first.
      .flatMap((row: FuelEURow): any[] =>
        isBioBlend(row)
          ? expandBioBlend(row)
          : [
              {
                fuel_factor: row.fuel_factor,
                quantity: Number(row.quantity),
                intensity_override: numberOrNull(row.intensity_override),
                lcv_override: numberOrNull(row.lcv_override),
                e_value: numberOrNull(row.e_value),
                blend_role: null,
              },
            ]
      )
      .map(shape);

  return {
    name_of_vessel: values.name_of_vessel,
    imo_number: values.imo_number === "" ? null : values.imo_number,
    year: values.year,
    days: elapsed + plan || DEFAULT_DAYS,
    elapsed_days: elapsed,
    // Posted in kWh per day, the unit an OPS connection is metered in. The API
    // converts to MJ (`fueleu.ops_energy_mj`) — deliberately not done here, so a
    // client that forgets cannot silently understate the figure 3.6x.
    ops_energy_kwh_per_day: numberOrNull(values.ops_energy_kwh_per_day) ?? 0,
    other_energy_per_day: 0,
    alternative_fuel: values.alternative_fuel,
    alternative_intensity_override: numberOrNull(
      values.alternative_intensity_override
    ),
    alternative_lcv_override: numberOrNull(values.alternative_lcv_override),
    alternative_e_value: numberOrNull(values.alternative_e_value),
    fuels: [
      ...expand(values.fuels, asPlanned),
      ...(elapsed ? expand(values.consumed_fuels, asConsumed) : []),
    ],
  };
};

/** Compute an already-built payload without storing it. */
export const postDailyPreview = async (
  payload: any
): Promise<FuelEUDailyResult> => {
  const res = await globalAPI.post(
    `${queryKeys.FuelEUDailyCalculator.url()}preview/`,
    payload
  );
  return res.data;
};

/** Store the plan, so the lead form has a record to attach itself to. */
export const saveFuelEUDaily = async (values: any) => {
  const res = await globalAPI.post(
    queryKeys.FuelEUDailyCalculator.url(),
    buildDailyPayload(values)
  );
  return res.data;
};

/* ------------------------------------------------------------ formatting */

/** An empty already-consumed row, same shape as a planned one. */
export const emptyConsumedRow = emptyFuelRow;

/** "1 day" / "90 days", so the horizon never reads "1 days". */
export const formatDays = (days: any): string => {
  const number = Number(days);
  if (!Number.isFinite(number)) return "—";
  return `${number} ${number === 1 ? "day" : "days"}`;
};

/**
 * A requirement in tonnes, or the sentence that explains its absence.
 *
 * Null is two different pieces of news — already compliant, or out of reach of
 * this fuel — and the caller has the reason to hand, so nothing here guesses.
 */
export const formatRequirement = (
  mass: number | null,
  decimals = 2
): string => {
  if (mass === null || !Number.isFinite(Number(mass))) return "—";
  return `${Number(mass).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} mt`;
};
